import { useRef, useEffect } from 'react';

/**
 * Manages contentEditable refs, debounced updates, and format commands
 * for the intro/outro/cancel rich text fields.
 */
export function useRichTextFields({
  introText, setIntroText,
  outroText, setOutroText,
  cancelText, setCancelText,
  cancelOutroText, setCancelOutroText,
  showIntroField, showOutroField, showCancelField
}) {
  const introTextRef = useRef(null);
  const outroTextRef = useRef(null);
  const cancelTextRef = useRef(null);
  const cancelOutroTextRef = useRef(null);
  const isTypingIntro = useRef(false);
  const isTypingOutro = useRef(false);
  const isTypingCancel = useRef(false);
  const isTypingCancelOutro = useRef(false);
  const introTypingTimeout = useRef(null);
  const outroTypingTimeout = useRef(null);
  const cancelTypingTimeout = useRef(null);
  const cancelOutroTypingTimeout = useRef(null);

  // Set initial content when fields are expanded
  useEffect(() => {
    if (showIntroField && introTextRef.current && introTextRef.current.innerHTML !== introText) {
      introTextRef.current.innerHTML = introText;
    }
  }, [showIntroField, introText]);

  useEffect(() => {
    if (showOutroField && outroTextRef.current && outroTextRef.current.innerHTML !== outroText) {
      outroTextRef.current.innerHTML = outroText;
    }
  }, [showOutroField, outroText]);

  useEffect(() => {
    if (showCancelField && cancelTextRef.current && cancelTextRef.current.innerHTML !== cancelText) {
      cancelTextRef.current.innerHTML = cancelText;
    }
  }, [showCancelField, cancelText]);

  useEffect(() => {
    if (showCancelField && cancelOutroTextRef.current && cancelOutroTextRef.current.innerHTML !== cancelOutroText) {
      cancelOutroTextRef.current.innerHTML = cancelOutroText;
    }
  }, [showCancelField, cancelOutroText]);

  // Debounced update functions to prevent cursor jumping
  const debouncedIntroUpdate = (value) => {
    if (introTypingTimeout.current) {
      clearTimeout(introTypingTimeout.current);
    }
    introTypingTimeout.current = setTimeout(() => {
      setIntroText(value);
      isTypingIntro.current = false;
    }, 300);
  };

  const debouncedOutroUpdate = (value) => {
    if (outroTypingTimeout.current) {
      clearTimeout(outroTypingTimeout.current);
    }
    outroTypingTimeout.current = setTimeout(() => {
      setOutroText(value);
      isTypingOutro.current = false;
    }, 300);
  };

  const debouncedCancelUpdate = (value) => {
    if (cancelTypingTimeout.current) {
      clearTimeout(cancelTypingTimeout.current);
    }
    cancelTypingTimeout.current = setTimeout(() => {
      setCancelText(value);
      isTypingCancel.current = false;
    }, 300);
  };

  const debouncedCancelOutroUpdate = (value) => {
    if (cancelOutroTypingTimeout.current) {
      clearTimeout(cancelOutroTypingTimeout.current);
    }
    cancelOutroTypingTimeout.current = setTimeout(() => {
      setCancelOutroText(value);
      isTypingCancelOutro.current = false;
    }, 300);
  };

  // Apply formatting to intro/outro text fields
  const applyIntroFormat = (tag) => {
    const element = introTextRef.current;
    if (!element) return;
    if (element.contentEditable === 'true') {
      if (tag === 'b') document.execCommand('bold', false, null);
      if (tag === 'i') document.execCommand('italic', false, null);
      if (tag === 'u') document.execCommand('underline', false, null);
      setIntroText(element.innerHTML);
    }
  };

  const applyOutroFormat = (tag) => {
    const element = outroTextRef.current;
    if (!element) return;
    if (element.contentEditable === 'true') {
      if (tag === 'b') document.execCommand('bold', false, null);
      if (tag === 'i') document.execCommand('italic', false, null);
      if (tag === 'u') document.execCommand('underline', false, null);
      setOutroText(element.innerHTML);
    }
  };

  const applyCancelFormat = (tag) => {
    const element = cancelTextRef.current;
    if (!element) return;
    if (element.contentEditable === 'true') {
      if (tag === 'b') document.execCommand('bold', false, null);
      if (tag === 'i') document.execCommand('italic', false, null);
      if (tag === 'u') document.execCommand('underline', false, null);
      setCancelText(element.innerHTML);
    }
  };

  const applyCancelOutroFormat = (tag) => {
    const element = cancelOutroTextRef.current;
    if (!element) return;
    if (element.contentEditable === 'true') {
      if (tag === 'b') document.execCommand('bold', false, null);
      if (tag === 'i') document.execCommand('italic', false, null);
      if (tag === 'u') document.execCommand('underline', false, null);
      setCancelOutroText(element.innerHTML);
    }
  };

  return {
    introTextRef, outroTextRef, cancelTextRef, cancelOutroTextRef,
    isTypingIntro, isTypingOutro, isTypingCancel, isTypingCancelOutro,
    introTypingTimeout, outroTypingTimeout, cancelTypingTimeout, cancelOutroTypingTimeout,
    debouncedIntroUpdate, debouncedOutroUpdate, debouncedCancelUpdate, debouncedCancelOutroUpdate,
    applyIntroFormat, applyOutroFormat, applyCancelFormat, applyCancelOutroFormat
  };
}
